import { Button } from "@datamole/wds-component-button";
import { Card } from "@datamole/wds-component-card";
import { Dialog } from "@datamole/wds-component-dialog";
import { faTrash, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext } from "react";

import { getAvatarSrc } from "../../../utils/person.utils";

import styles from "./entity-card.module.css";

import { seatConfigurationService } from "@/features/administration/seat-configuration/seat-configuration.service";
import { useRole } from "@/features/auth/role.hooks";
import { Label } from "@/features/info-popover/entity-cards/label";
import { EmployeeTrigger, InfoPopoverContext } from "@/features/info-popover/popover.context";
import { NavigateAction } from "@/features/navigation/NavigateAction";
import { storageService } from "@/features/storage/storage.service";
import { useStorageSubscription } from "@/features/storage/use-storage-subscription";

type Props = {
    trigger: EmployeeTrigger;
};

export const EmployeeHeader = ({ trigger }: Props) => {
    const employee = storageService.getEmployees().find((e) => e.id === trigger.employeeId);
    if (!employee) return null;

    return (
        <Card.Header.Description>
            <Card.Header.Description.Title style={{ fontSize: 24 }}>
                <FontAwesomeIcon icon={faUser} style={{ marginRight: 16, color: "var(--wds-color-primary-700)" }} />
                {employee.name}
            </Card.Header.Description.Title>
        </Card.Header.Description>
    );
};

export const EmployeeCard = ({ trigger }: Props) => {
    useStorageSubscription();

    const employee = storageService.getEmployees().find((e) => e.id === trigger.employeeId);
    if (!employee) return null;

    const seatInfo = employee.seat !== "Unassigned" ? storageService.getSeatById(employee.seat) : null;

    return (
        <div className={styles.card_info_container}>
            <img
                className={styles.card_image}
                src={getAvatarSrc(employee.avatar)}
                width={90}
                height={90}
                alt="avatar"
            />
            <div className={styles.card_info}>
                {employee.department !== undefined && (
                    <div>
                        <Label>Department:</Label> {employee.department}
                    </div>
                )}
                {employee.team !== undefined && (
                    <div>
                        <Label>Team:</Label> {employee.team}
                    </div>
                )}
                <div>
                    <Label>Position:</Label> {employee.position}
                </div>
                {seatInfo ? (
                    <div>
                        <Label>Location:</Label> Floor {seatInfo.floorId.replace("floor-", "")}, {seatInfo.roomName}
                    </div>
                ) : (
                    <div>
                        <Label>Location:</Label> No seat assigned
                    </div>
                )}
            </div>
        </div>
    );
};

export const EmployeeActions = ({ trigger }: Props) => {
    const { isAdmin } = useRole();
    const { setTrigger } = useContext(InfoPopoverContext);

    const employee = storageService.getEmployees().find((e) => e.id === trigger.employeeId);
    if (!employee) return null;

    const seatInfo = employee.seat !== "Unassigned" ? storageService.getSeatById(employee.seat) : null;
    const startRoomId = storageService.getMyLocation()?.roomId;

    const actions = [];

    if (isAdmin && seatInfo) {
        actions.push(
            <Dialog key="empty-seat">
                <Dialog.Trigger asChild>
                    <Button>
                        <Button.Icon>
                            <FontAwesomeIcon icon={faTrash} />
                        </Button.Icon>
                        <Button.Content>Empty Seat</Button.Content>
                    </Button>
                </Dialog.Trigger>
                <Dialog.Content>
                    <Dialog.Content.Header>Empty Seat</Dialog.Content.Header>
                    <Dialog.Content.Body>
                        <p>Are you sure you want to empty this seat?</p>
                    </Dialog.Content.Body>
                    <Dialog.Content.Actions>
                        <Dialog.Content.Actions.Close asChild>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    seatConfigurationService.removeEmployeeFromSeat(seatInfo.id);
                                    setTrigger(null);
                                }}
                            >
                                Yes, Empty Seat
                            </Button>
                        </Dialog.Content.Actions.Close>
                        <Dialog.Content.Actions.Close asChild>
                            <Button>No, Cancel</Button>
                        </Dialog.Content.Actions.Close>
                    </Dialog.Content.Actions>
                </Dialog.Content>
            </Dialog>,
        );
    }

    if (seatInfo && startRoomId) {
        actions.push(<NavigateAction key="navigate-action" startRoomId={startRoomId} endRoomId={seatInfo.room.id} />);
    }

    return <>{actions}</>;
};
